<template>
</template>
<script>
import $ from 'jquery';
import { mapState } from 'vuex';
import { buildPtDetailItem, ensureGtmLoaded, pushEcommerceEvent, resolvePrice } from 'assets/js/gtm';

// クリック後は最大 20 秒待機し、カート画面へ遷移しない場合は add_to_cart の送信を中止する。
const PENDING_EXPIRE_MS = 20000;
// モジュールスコープの変数で「送信待ちイベント」を保持し、コンポーネント破棄時の状態消失を防ぐ。
let pendingAddToCartEvent = null;

// カート画面判定: 本プロジェクトでは pageNo=16 をカート画面として扱う。
function isCartRoute(route) {
	const pageNo = route && route.query ? String(route.query.pageNo || '') : '';
	return pageNo === '16';
}

// 古いクリックイベントが遅延して誤送信されることを防ぐ。
function isPendingExpired(pending) {
	return !pending || !pending.queuedAt || (Date.now() - pending.queuedAt > PENDING_EXPIRE_MS);
}

// 送信待ちイベントをクリアし、イベントの混在を防ぐ。
function clearPendingAddToCartEvent() {
	pendingAddToCartEvent = null;
}

export default {
	name: "PT501GTMAddToCart",
	computed: {
		...mapState('pt120', ['pt120Init', 'pt120ReqModel']),
		...mapState('pt125', ['pt125Init', 'pt125ReqModel']),
		isPt125() {
			return !!(this.pt125Init && Array.isArray(this.pt125Init.detailList) && this.pt125Init.detailList.length);
		},
		productInfo() {
			if (this.isPt125) {
				return this.pt125Init.productInfo ? this.pt125Init.productInfo : {};
			}
			return this.pt120Init && this.pt120Init.productInfo ? this.pt120Init.productInfo : {};
		},
		navList() {
			if (this.isPt125) {
				return this.pt125Init.navList ? this.pt125Init.navList : [];
			}
			return this.pt120Init && this.pt120Init.navList ? this.pt120Init.navList : [];
		},
		reqModel() {
			if (this.isPt125) {
				return this.pt125ReqModel || {};
			}
			return this.pt120ReqModel || {};
		},
		gtmContainerId() {
			if (this.isPt125) {
				return this.pt125Init.gtmcontainerId || '';
			}
			return this.pt120Init.gtmcontainerId || ''
		},
		quantity() {
			const quantity = Number(this.reqModel && this.reqModel.buyCount);
			return Number.isNaN(quantity) || quantity <= 0 ? 1 : quantity;
		},
		eventPayloadReady() {
			return !!this.gtmContainerId && !!this.productInfo.S_ITEM_NO;
		}
	},
	watch: {
		eventPayloadReady: {
			handler(ready) {
				if (ready) {
					this.$nextTick(() => {
						this.initGtmContainer();
					})
				}
			},
			immediate: true,
		}
	},
	mounted() {
		this.$nextTick(() => {
			if (!this.hasAddToCartButtons()) {
				return;
			}
			// ルート遷移後フックはグローバルで一度だけ登録し、カート画面への遷移成功を判定する。
			this.ensureRouterAfterEachHook();
			this.initGtmContainer();
			// ここでは「送信待ちイベントの保持」だけを行い、即時送信はしない。
			$(document).on('click', '#itemCartButton', this.addToCartHandler);
			$(document).on('click', '#itemCartTeikiButton', this.addToCartTeikiHandler);
		});
	},
	destroyed() {
		$(document).off('click', '#itemCartButton', this.addToCartHandler);
		$(document).off('click', '#itemCartTeikiButton', this.addToCartTeikiHandler);
	},
	methods: {
		hasAddToCartButtons() {
			return !!document.querySelector('#itemCartButton, #itemCartTeikiButton');
		},
		canRunInContext() {
			return this.hasAddToCartButtons();
		},
		initGtmContainer() {
			if (!this.eventPayloadReady || !this.canRunInContext()) {
				return;
			}
			this.$store.commit('setGtmId', this.gtmContainerId);
			// GTM コンテナの初期化を保証する。
			ensureGtmLoaded();
		},
		ensureRouterAfterEachHook() {
			// afterEach の重複登録を防ぎ、1回の遷移で複数回送信されることを回避する。
			if (this.$router.__gtmAddToCartAfterEachHookInstalled) {
				return;
			}
			this.$router.__gtmAddToCartAfterEachHookInstalled = true;
			this.$router.afterEach((to, from) => {
				// 送信待ちイベントがなければ終了。
				if (!pendingAddToCartEvent) {
					return;
				}
				// タイムアウト済みのイベントは送信しない。
				if (isPendingExpired(pendingAddToCartEvent)) {
					clearPendingAddToCartEvent();
					return;
				}
				// カート画面への遷移成功時のみ add_to_cart を送信する。
				if (isCartRoute(to)) {
					const pending = pendingAddToCartEvent;
					clearPendingAddToCartEvent();
					pushEcommerceEvent('add_to_cart', pending.ecommerce);
					return;
				}
				// ルート遷移は発生したがカート画面ではない場合、このクリックは失敗としてクリアする。
				if (to && from && to.fullPath !== from.fullPath) {
					clearPendingAddToCartEvent();
				}
			});
		},
		addToCartHandler() {
			if (!this.canRunInContext()) {
				return;
			}
			this.queueAddToCart(false);
		},
		addToCartTeikiHandler() {
			if (!this.canRunInContext()) {
				return;
			}
			this.queueAddToCart(true);
		},
		// GA4 add_to_cart 用の ecommerce payload を構築する。
		buildAddToCartEcommerce(teiki = false) {
			const item = buildPtDetailItem(this.productInfo, this.navList, teiki);
			return {
				currency: 'JPY',
				value: resolvePrice(this.productInfo, teiki) * this.quantity,
				items: [
					Object.assign({}, item, { quantity: this.quantity }),
				],
			};
		},
		// クリック時は payload を保持のみ行い、afterEach で遷移成功を確認後に送信する。
		queueAddToCart(teiki = false) {
			if (!this.eventPayloadReady || !this.canRunInContext()) {
				return;
			}
			pendingAddToCartEvent = {
				queuedAt: Date.now(),
				ecommerce: this.buildAddToCartEcommerce(teiki),
			};
		}
	}
};

</script>
